import { useQuery, useMutation } from '@apollo/client';
import React, { useState, useEffect } from 'react';
import { ThemeButton, toast } from '@ibinder/ui-common-core';
import { buttonTypes } from '@ibinder/ui-common-styles';
import RegisterHeader from '../../../AdminBinder/AdminContentContainer/RegisterHeader';

import { GeneralSettings } from './components/GeneralSettings';
import { Users } from './components/Users';
import { FormatGroups } from './components/FormatGroups';
import { PrintOptions } from './components/PrintOptions';
import { PostProcessingOptions } from './components/PostProcessingOptions';
import {
  GET_PRINT_PROVIDER_BY_ID,
  GET_PRINT_SETTINGS
} from '../../api/queries';
import {
  UPDATE_PRINT_PROVIDER,
  CREATE_PRINT_PROVIDER
} from '../../api/mutations';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { PrintProviderType, PrintSettingsType } from '../../types/common.types';
import { defaultProviderData } from '../../constants/default';

import { printApiRestClient } from '../../api/rest.configuration';

import { PrintProviderTab, NavigationTabs } from './components/NavigationTabs';
import { ContentContainer, ActionsBar } from './PrintProviderDetails.styles';
import { getProvidersListTab } from '../../helpers/getProvidersListTab';
import { formatDefault, formatPrintProviderData } from '../../helpers/format';
import { CommonProvider } from '../../context/CommonProvider';
import { validate as isValidUUID } from 'uuid';

export const PrintProviderDetails = ({ toggleTabsMenu, rightPanelOpen }) => {
  const { providerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = useQuery<{
    getPrintProviderById: PrintProviderType;
  }>(GET_PRINT_PROVIDER_BY_ID, {
    client: printApiRestClient,
    fetchPolicy: 'no-cache',
    skip: !isValidUUID(providerId),
    variables: {
      printProviderId: providerId
    }
  });
  const { data: printSettings } = useQuery<{
    getPrintSettings: PrintSettingsType;
  }>(GET_PRINT_SETTINGS, {
    client: printApiRestClient
  });

  const [printProviderData, updatePrintProviderData] =
    useState<PrintProviderType>(defaultProviderData);
  const onChangeData = (id: string, value: any): void => {
    updatePrintProviderData((prevData) => ({
      ...prevData,
      [id]: value
    }));
  };

  const [updateProvider] = useMutation(UPDATE_PRINT_PROVIDER, {
    client: printApiRestClient
  });
  const [createProvicder] = useMutation(CREATE_PRINT_PROVIDER, {
    client: printApiRestClient
  });

  const [activeTab, setActiveTab] = useState(PrintProviderTab.GeneralSettings);

  const getCurrentTabContent = () => {
    switch (activeTab) {
      case PrintProviderTab.GeneralSettings:
        return (
          <GeneralSettings
            reqData={data?.getPrintProviderById}
            data={printProviderData}
            updateData={onChangeData}
          />
        );
      case PrintProviderTab.Users:
        return <Users data={printProviderData} updateData={onChangeData} />;
      case PrintProviderTab.FormatGroups:
        return (
          <FormatGroups
            printProvider={printProviderData}
            printSettings={printSettings?.getPrintSettings}
            updateManually={updatePrintProviderData}
          />
        );
      case PrintProviderTab.PrintOptions:
        return (
          <PrintOptions
            printProvider={printProviderData}
            printSettings={printSettings?.getPrintSettings}
            updateManually={updatePrintProviderData}
          />
        );
      case PrintProviderTab.PostProcessingOptions:
        return (
          <PostProcessingOptions
            printProvider={printProviderData}
            printSettings={printSettings?.getPrintSettings}
            updateManually={updatePrintProviderData}
          />
        );
      default:
        return <></>;
    }
  };

  const getActionsBarContent = () => {
    return (
      <>
        <ThemeButton
          buttonType={buttonTypes.Default}
          onClick={() => onCancelEdit()}
        >
          Cancel
        </ThemeButton>{' '}
        <ThemeButton buttonType={buttonTypes.Primary} onClick={() => onSave()}>
          Save
        </ThemeButton>
      </>
    );
  };

  const onSave = () => {
    if (location.pathname.split('/').at(-1) === 'add') {
      onCreateSave();
    } else {
      onEditSave();
    }
  };

  const onCreateSave = async () => {
    if (printProviderData) {
      await createProvicder({
        client: printApiRestClient,
        variables: {
          body: formatPrintProviderData(printProviderData)
        }
      })
        .then(() => {
          redirectToProvidersList();
        })
        .catch(() => {
          toast.error('Something went wrong');
        });
    }
  };

  const onEditSave = async () => {
    if (printProviderData && isValidUUID(providerId)) {
      await updateProvider({
        client: printApiRestClient,
        variables: {
          printProviderId: printProviderData.printProviderId,
          body: formatPrintProviderData(printProviderData)
        }
      })
        .then(() => {
          redirectToProvidersList();
        })
        .catch(() => {
          toast.error('Something went wrong');
        });
    }
  };

  const redirectToProvidersList = () => {
    navigate(`/tab/${getProvidersListTab().id}`);
  };

  const onCancelEdit = () => {
    redirectToProvidersList();
  };

  const navigationTabsData = {
    parent: getProvidersListTab(),
    value: { name: printProviderData?.name }
  };

  useEffect(() => {
    if (data) {
      updatePrintProviderData(data?.getPrintProviderById);
    }

    if (printSettings?.getPrintSettings && !data) {
      updatePrintProviderData((prev) => ({
        ...prev,
        ...formatDefault(printProviderData, printSettings?.getPrintSettings)
      }));
    }
  }, [data, printSettings]);

  return (
    <CommonProvider>
      <RegisterHeader
        tabs={navigationTabsData}
        rightPanelOpen={rightPanelOpen}
        toggleTabsMenu={toggleTabsMenu}
        nodeData={navigationTabsData}
      />
      <NavigationTabs activeTab={activeTab} onChangeActiveTab={setActiveTab} />
      <ContentContainer>{getCurrentTabContent()}</ContentContainer>
      <ActionsBar>{getActionsBarContent()}</ActionsBar>
    </CommonProvider>
  );
};
